import { isAaveV4Type } from '@1delta/lender-registry'
import type { TermAdapter } from './index'

/**
 * Aave V4 — the liquidation model the generic builder cannot express.
 *
 * Every other pool lender we serve answers "how much of my debt can be taken
 * in one go?" with a constant: Aave V3 halves it, Compound V2 reads
 * `closeFactorMantissa`, isolated markets allow the lot. **V4 has no close
 * factor at all.** A liquidator repays however much it takes to restore the
 * position to the spoke's `targetHealthFactor`, and the bonus scales with how
 * far under water it is rather than being a fixed percentage.
 *
 * Two consequences, and the sheet was getting both wrong:
 *
 *  1. **`closeFactor: 0.5` is not a V4 parameter.** The public-data layer
 *     stamps it as an explicit cross-lender shape default (see the
 *     `aave-v4-type` README, "Liquidation output fields") — which is fine as a
 *     column filler and wrong as a published term, because it states a 50 %
 *     rule the protocol does not have while a real V4 liquidation can take the
 *     whole debt. The honest ceiling is 1, with the sizing rule stated instead
 *     of a number.
 *
 *  2. **The penalty is a MAXIMUM, not a rate.** `liquidationPenalty` comes from
 *     `DynamicReserveConfig.maxLiquidationBonus`, which is only reached at
 *     `healthFactorForMaxBonus`; between there and HF 1 the bonus is
 *     interpolated by `liquidationBonusFactor`. Left as the bare headline it
 *     reads as what every liquidation charges.
 *
 * `targetHealthFactor` reaches us on the market row (`caps.targetHealthFactor`
 * on `/lending/latest`, flat on `/pools/latest`) and is populated on all 12
 * Ethereum spokes — 1.0137 to 1.3075, i.e. genuinely per-spoke and not a
 * constant worth hardcoding. The bonus curve is only present when the
 * `aaveV4` descriptor is carried; it degrades to the max-bonus statement alone
 * rather than inventing a threshold.
 */
export const aaveV4Adapter: TermAdapter = {
  id: 'aave-v4',
  matches: isAaveV4Type,
  profileId: () => 'aave-v4.spoke@v1',
  build: (input) => {
    const target = input.targetHealthFactor
    const desc = (input.market?.aaveV4 ?? {}) as Record<string, unknown>
    const maxBonusHf = num(desc.healthFactorForMaxBonus)

    return {
      borrow: {
        liquidation: {
          model: 'repay-to-target-hf',
          // NOT a close factor — the ceiling. See the header.
          closeFactor: 1,
          targetHealthFactor: target,
          healthFactorForMaxBonus: maxBonusHf,
        },
      },
    }
  },
}

function num(v: unknown): number | undefined {
  if (v == null) return undefined
  const n = typeof v === 'string' ? Number(v) : (v as number)
  return Number.isFinite(n) && n > 0 ? n : undefined
}
